import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { RefreshCw, Search, Filter, Trash2 } from "lucide-react";
import { adminApi } from "../../lib/api";

const TYPE_STYLES = {
  b2b_contact: "bg-blue-100 text-blue-800",
  b2b_demo: "bg-indigo-100 text-indigo-800",
  b2b_signup: "bg-green-100 text-green-800",
  b2c_fallback: "bg-amber-100 text-amber-800",
  affiliate_signup: "bg-purple-100 text-purple-800",
  general: "bg-slate-100 text-slate-700",
};

const TYPE_FILTERS = [
  { id: "all", label: "All" },
  { id: "b2b", label: "B2B (contact + demo + signup)" },
  { id: "b2b_contact", label: "B2B Contact" },
  { id: "b2b_demo", label: "B2B Demo" },
  { id: "b2b_signup", label: "B2B Signup" },
  { id: "b2c_fallback", label: "B2C Fallback" },
  { id: "affiliate_signup", label: "Affiliate Signup" },
];

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await adminApi().put(`/admin/leads/${id}/status`, { status });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (visibleIds) => {
    setSelected((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await adminApi().delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const deleteBulk = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await adminApi().post("/admin/leads/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter === "b2b") {
        if (!["b2b_contact", "b2b_demo", "b2b_signup"].includes(l.lead_type)) return false;
      } else if (filter !== "all" && l.lead_type !== filter) return false;
      if (term) {
        const hay = `${l.name || ""} ${l.email || ""} ${l.phone || ""} ${l.company || ""} ${l.message || ""} ${l.source_page || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [leads, filter, q]);

  const counts = useMemo(() => {
    const c = { total: leads.length, b2b: 0, b2c: 0, affiliate: 0, other: 0 };
    leads.forEach((l) => {
      if (["b2b_contact", "b2b_demo", "b2b_signup"].includes(l.lead_type)) c.b2b += 1;
      else if (l.lead_type === "b2c_fallback") c.b2c += 1;
      else if (l.lead_type === "affiliate_signup") c.affiliate += 1;
      else c.other += 1;
    });
    return c;
  }, [leads]);

  const latest = leads[0]?.created_at;
  const visibleIds = filtered.map((l) => l.id);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  return (
    <div className="space-y-4" data-testid="leads-tab">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <span data-testid="leads-total-count"><b className="text-slate-900">{counts.total}</b> total</span>
          <span className="text-blue-700"><b>{counts.b2b}</b> B2B</span>
          <span className="text-amber-700"><b>{counts.b2c}</b> B2C</span>
          <span className="text-purple-700"><b>{counts.affiliate}</b> Affiliate</span>
          {latest && (
            <span className="text-slate-400 hidden sm:inline">
              Latest: <b className="text-slate-700">{timeAgo(latest)}</b>
            </span>
          )}
        </div>
        <button
          data-testid="leads-refresh-btn"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#970C10] disabled:opacity-40 transition-colors duration-200"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="leads-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, company, message…"
            className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#970C10]/30"
          />
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1 overflow-x-auto no-scrollbar">
          <Filter className="w-3.5 h-3.5 text-slate-400 ml-1.5" />
          {TYPE_FILTERS.map((t) => (
            <button
              key={t.id}
              data-testid={`leads-filter-${t.id}`}
              onClick={() => setFilter(t.id)}
              className={`shrink-0 px-2.5 py-1 rounded text-[11px] font-bold whitespace-nowrap transition-colors duration-200 ${
                filter === t.id ? "bg-[#970C10] text-white" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="leads-bulk-bar"
          className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-md px-4 py-2.5"
        >
          <p className="text-sm text-red-900">
            <b>{selected.size}</b> selected
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs font-bold text-red-800 hover:text-red-950 transition-colors duration-200"
              data-testid="leads-bulk-clear"
            >
              Clear
            </button>
            <button
              data-testid="leads-bulk-delete"
              onClick={deleteBulk}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
            >
              <Trash2 className="w-3.5 h-3.5" /> {deleting ? "Deleting…" : "Delete selected"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="pl-4 py-3 w-6">
                <input
                  type="checkbox"
                  data-testid="leads-select-all"
                  checked={allChecked}
                  onChange={() => toggleAll(visibleIds)}
                  className="cursor-pointer accent-[#970C10]"
                />
              </th>
              <th className="px-3 py-3">Received</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-leads-table">
            {filtered.map((l) => (
              <tr key={l.id} className={`border-t border-slate-100 align-top hover:bg-slate-50/70 transition-colors duration-150 ${selected.has(l.id) ? "bg-red-50/40" : ""}`} data-testid={`lead-row-${l.id}`}>
                <td className="pl-4 py-3">
                  <input
                    type="checkbox"
                    data-testid={`lead-check-${l.id}`}
                    checked={selected.has(l.id)}
                    onChange={() => toggleOne(l.id)}
                    className="cursor-pointer accent-[#970C10]"
                  />
                </td>
                <td className="px-3 py-3 text-xs">
                  <p className="font-semibold text-slate-800">{timeAgo(l.created_at)}</p>
                  <p className="text-[10px] text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${TYPE_STYLES[l.lead_type] || "bg-slate-100 text-slate-600"}`}>
                    {l.lead_type}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold">
                  {l.name}
                  {l.company && <span className="block text-xs text-slate-400 font-normal">{l.company}</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  <a href={`mailto:${l.email}`} className="text-slate-700 hover:text-[#970C10]">{l.email}</a>
                  {l.phone && <p className="text-slate-500 mt-0.5">{l.phone}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 max-w-xs whitespace-pre-wrap">{l.message}</td>
                <td className="px-4 py-3 text-xs font-mono">
                  {l.source_page}
                  {l.attribution?.utm_source && (
                    <span className="block text-[10px] text-[#970C10] font-bold mt-1">
                      {l.attribution.utm_source}{l.attribution.utm_campaign ? ` · ${l.attribution.utm_campaign}` : ""}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={l.status}
                    onChange={(e) => setStatus(l.id, e.target.value)}
                    className="text-xs border border-slate-200 rounded px-2 py-1"
                    data-testid={`lead-status-${l.id}`}
                  >
                    {["new", "contacted", "converted", "closed"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-2 py-3">
                  <button
                    data-testid={`lead-delete-${l.id}`}
                    onClick={() => deleteOne(l.id)}
                    title="Delete lead"
                    className="text-slate-400 hover:text-red-600 transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="leads-empty">
                  {leads.length === 0 ? "No leads yet — the form submissions will show here." : "No leads match this filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
